/**
 * Custom Navigation — Hamdan Electronics
 * Warehouse 7.0.1 theme customization
 *
 * Components:
 * 1. NavBackdrop — Shared backdrop overlay with floating X cursor (desktop)
 * 2. AllCategoriesDropdown — "All Categories" button + full mega dropdown
 * 3. InlineNavInterceptor — Monitors inline nav dropdowns to show/hide backdrop
 * 4. CustomMobileMenu — Mobile drawer backdrop management
 */

/* ==========================================================================
   1. NavBackdrop — Shared desktop backdrop with floating X cursor
   ========================================================================== */

const NavBackdrop = {
  element: null,
  cursor: null,
  closeCallback: null,
  _initialized: false,

  init() {
    // Check if already initialized and elements still in DOM
    if (this._initialized && this.element && document.body.contains(this.element)) return;

    // Clean up stale elements from previous initialization
    if (this.element && this.element.parentNode) this.element.remove();
    if (this.cursor && this.cursor.parentNode) this.cursor.remove();

    this._initialized = true;

    // Create backdrop
    this.element = document.createElement('div');
    this.element.className = 'custom-nav-backdrop';
    document.body.appendChild(this.element);

    // Create floating cursor with X icon
    this.cursor = document.createElement('div');
    this.cursor.className = 'custom-nav-cursor';
    this.cursor.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
    document.body.appendChild(this.cursor);

    // Track mouse on backdrop
    this.element.addEventListener('mousemove', (e) => {
      requestAnimationFrame(() => {
        this.cursor.style.left = e.clientX + 'px';
        this.cursor.style.top = e.clientY + 'px';
      });
      if (!this.cursor.classList.contains('is-visible')) {
        this.cursor.classList.add('is-visible');
      }
    });

    this.element.addEventListener('mouseleave', () => {
      this.cursor.classList.remove('is-visible');
    });

    // Click backdrop to close
    this.element.addEventListener('click', () => {
      if (this.closeCallback) {
        this.closeCallback();
      }
    });
  },

  show(onClose) {
    this.closeCallback = onClose;
    this.element.classList.add('is-visible');
  },

  hide() {
    this.element.classList.remove('is-visible');
    this.cursor.classList.remove('is-visible');
    this.closeCallback = null;
  }
};

/* ==========================================================================
   2. AllCategoriesDropdown
   ========================================================================== */

class AllCategoriesDropdown {
  constructor() {
    this.button = document.querySelector('[data-action="toggle-all-categories"]');
    this.dropdown = document.querySelector('[data-custom-all-categories-dropdown]');
    this.isOpen = false;

    if (!this.button || !this.dropdown) return;
    this._attachListeners();
    this._initPanelSwitching();
  }

  _attachListeners() {
    this.button.addEventListener('click', (e) => {
      e.preventDefault();
      this._toggle();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this._close();
      }
    });

    // Close mega menu when search bar receives focus
    const searchInput = document.querySelector('.search-bar__input');
    if (searchInput) {
      searchInput.addEventListener('focus', () => {
        if (this.isOpen) this._close();
      });
    }
  }

  _toggle() {
    if (this.isOpen) {
      this._close();
    } else {
      this._open();
    }
  }

  _open() {
    // Close any open inline nav dropdowns first
    this._closeInlineDropdowns();

    this.button.setAttribute('aria-expanded', 'true');
    this.dropdown.setAttribute('aria-hidden', 'false');
    this.isOpen = true;

    NavBackdrop.show(() => this._close());
  }

  _close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.dropdown.setAttribute('aria-hidden', 'true');
    this.isOpen = false;

    NavBackdrop.hide();
  }

  _initPanelSwitching() {
    const sidebar = this.dropdown.querySelector('.custom-ac-sidebar');
    if (!sidebar) return;

    const items = sidebar.querySelectorAll('[data-ac-category]');
    const panels = this.dropdown.querySelectorAll('[data-ac-panel]');

    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        const index = item.dataset.acCategory;

        // Update sidebar active state
        items.forEach((i) => i.classList.remove('is-active'));
        item.classList.add('is-active');

        // Switch content panel
        panels.forEach((p) => p.classList.remove('is-active'));
        const targetPanel = this.dropdown.querySelector(
          '[data-ac-panel="' + index + '"]'
        );
        if (targetPanel) targetPanel.classList.add('is-active');
      });
    });
  }

  _closeInlineDropdowns() {
    const navBar = document.querySelector('.nav-bar');
    if (!navBar) return;

    // Find all open dropdowns and close them by clicking the active nav item
    const openItems = navBar.querySelectorAll('.is-dropdown-open');
    openItems.forEach((item) => {
      const link = item.querySelector('[data-type="menuitem"][aria-expanded="true"]');
      if (link) {
        // Simulate click to use theme's _toggleDropdown
        link.click();
      }
    });
  }
}

/* ==========================================================================
   3. InlineNavInterceptor — Watch inline nav for open/close to manage backdrop
   ========================================================================== */

class InlineNavInterceptor {
  constructor(allCategoriesDropdown) {
    this.allCategories = allCategoriesDropdown;
    this.navBar = document.querySelector('.nav-bar');
    this._observing = false;

    if (!this.navBar) return;
    this._observe();
  }

  _observe() {
    // Watch all dropdown/mega-menu elements for aria-hidden changes
    const targets = this.navBar.querySelectorAll('.nav-dropdown, .mega-menu');
    if (targets.length === 0) return;

    this._observing = true;
    let wasOpen = false;

    this.observer = new MutationObserver(() => {
      const anyOpen = Array.from(targets).some(
        (t) => t.getAttribute('aria-hidden') === 'false'
      );

      if (anyOpen && !wasOpen) {
        // A dropdown just opened
        wasOpen = true;

        // Close All Categories if open
        if (this.allCategories && this.allCategories.isOpen) {
          // Directly close without triggering backdrop hide
          this.allCategories.button.setAttribute('aria-expanded', 'false');
          this.allCategories.dropdown.setAttribute('aria-hidden', 'true');
          this.allCategories.isOpen = false;
        }

        NavBackdrop.show(() => {
          // Close all inline dropdowns by clicking active items
          const openItems = this.navBar.querySelectorAll('.is-dropdown-open');
          openItems.forEach((item) => {
            const link = item.querySelector('[data-type="menuitem"][aria-expanded="true"]');
            if (link) link.click();
          });
          NavBackdrop.hide();
        });
      } else if (!anyOpen && wasOpen) {
        // All dropdowns just closed
        wasOpen = false;
        NavBackdrop.hide();
      }
    });

    targets.forEach((target) => {
      this.observer.observe(target, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });
    });
  }
}

/* ==========================================================================
   4. CustomMobileMenu — Mobile drawer backdrop management
   ========================================================================== */

class CustomMobileMenu {
  constructor() {
    this.mobileMenu = document.getElementById('mobile-menu');
    this.backdrop = null;

    if (!this.mobileMenu) return;
    this._createBackdrop();
    this._observe();
  }

  _createBackdrop() {
    // Check if backdrop already exists
    this.backdrop = document.querySelector('.custom-mobile-backdrop');
    if (!this.backdrop) {
      this.backdrop = document.createElement('div');
      this.backdrop.className = 'custom-mobile-backdrop';
      // Append inside the header section wrapper for correct stacking context
      // (header has z-index: 5, mobile menu is inside it — backdrop must be too)
      const sectionWrapper = this.mobileMenu.closest('.shopify-section__header')
        || this.mobileMenu.closest('[data-section-type="header"]')
        || document.body;
      sectionWrapper.appendChild(this.backdrop);
    }

    // Click backdrop to close drawer
    this.backdrop.addEventListener('click', () => {
      const toggleBtn = document.querySelector('.header__mobile-nav-toggle[data-action="toggle-menu"]');
      if (toggleBtn) toggleBtn.click();
    });
  }

  _observe() {
    // Watch mobile menu aria-hidden changes
    this.observer = new MutationObserver(() => {
      const isOpen = this.mobileMenu.getAttribute('aria-hidden') === 'false';
      this.backdrop.classList.toggle('is-visible', isOpen);
    });

    this.observer.observe(this.mobileMenu, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }
}

/* ==========================================================================
   5. Initialization
   ========================================================================== */

/* ==========================================================================
   5. TransparentHeader — Header transparency over hero slideshow
   ========================================================================== */

class TransparentHeader {
  constructor() {
    this.headerSection = document.querySelector('.shopify-section__header');
    // Support both slideshow and hero-banner sections
    this.hero = document.querySelector('.slideshow') || document.querySelector('.hero-banner');

    if (!this.headerSection || !this.hero) return;

    // Activate transparent mode
    this.headerSection.classList.add('header--over-hero');

    // Pull hero section up behind the header
    const heroWrapper = this.hero.closest('.shopify-section');
    if (heroWrapper) {
      heroWrapper.classList.add('slideshow-section--hero');
    }

    this._observe();
  }

  _observe() {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.headerSection.classList.add('header--over-hero');
            this.headerSection.classList.remove('header--scrolled');
          } else {
            this.headerSection.classList.remove('header--over-hero');
            this.headerSection.classList.add('header--scrolled');
          }
        });
      },
      {
        threshold: 0,
        rootMargin: '-80px 0px 0px 0px'
      }
    );

    observer.observe(this.hero);
  }
}

/* ==========================================================================
   6. Initialization
   ========================================================================== */

/* --------------------------------------------------------------------------
   Cart Price Updater
   Watches for cart count changes and updates the price display
   -------------------------------------------------------------------------- */

class CartPriceUpdater {
  constructor() {
    this.priceElements = document.querySelectorAll('[data-cart-total]');
    if (this.priceElements.length === 0) return;

    this.cartCountEl = document.querySelector('.header__cart-count');
    if (!this.cartCountEl) return;

    // Observe cart count text changes (theme.js updates this on cart change)
    this.observer = new MutationObserver(() => this.fetchCartTotal());
    this.observer.observe(this.cartCountEl, { childList: true, characterData: true, subtree: true });
  }

  fetchCartTotal() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(r => r.json())
      .then(cart => {
        const formatted = this.formatMoney(cart.total_price);
        this.priceElements.forEach(el => { el.textContent = formatted; });
      })
      .catch(() => {});
  }

  formatMoney(cents) {
    const format = window.theme && window.theme.moneyFormat ? window.theme.moneyFormat : '${{amount}}';
    const amount = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return format.replace(/\{\{\s*amount\s*\}\}/, amount)
                 .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100))
                 .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','));
  }
}

/* --------------------------------------------------------------------------
   Wishlist Hero Badge Updater
   Updates the wishlist count badge in the header
   -------------------------------------------------------------------------- */

class WishlistBadgeUpdater {
  constructor() {
    this.badges = document.querySelectorAll('[data-wishlist-count]');
    if (this.badges.length === 0) return;

    // Wishlist Hero fires 'WishlistHero:updated' when items change
    document.addEventListener('WishlistHero:updated', () => this.updateCount());
    // Also try on load in case app is already initialized
    this.checkOnLoad();
  }

  checkOnLoad() {
    // Poll briefly for WishlistHero global (app loads async)
    let attempts = 0;
    const check = () => {
      if (window.WishlistHero && window.WishlistHero.getWishlistItems) {
        this.updateCount();
      } else if (attempts < 10) {
        attempts++;
        setTimeout(check, 500);
      }
    };
    check();
  }

  updateCount() {
    try {
      const items = window.WishlistHero.getWishlistItems();
      const count = Array.isArray(items) ? items.length : 0;
      this.badges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });
    } catch (e) {
      // App not loaded yet or API changed — hide badges
    }
  }
}

/* --------------------------------------------------------------------------
   Mobile Search — Solid header when search is open
   -------------------------------------------------------------------------- */

class MobileSearchFix {
  constructor() {
    this.searchWrapper = document.querySelector('.header__search-bar-wrapper');
    this.headerSection = document.querySelector('.shopify-section__header');
    if (!this.searchWrapper || !this.headerSection) return;

    this.observer = new MutationObserver(() => {
      const isOpen = this.searchWrapper.classList.contains('is-fixed') || this.searchWrapper.classList.contains('is-visible');
      this.headerSection.classList.toggle('header--search-open', isOpen);

      // Strip is-fixed on mobile — theme.js adds it on input focus causing
      // position:absolute layout shift. We don't need it on mobile.
      if (window.innerWidth < 1000 && this.searchWrapper.classList.contains('is-fixed')) {
        this.searchWrapper.classList.remove('is-fixed');
        document.documentElement.classList.remove('is-locked');
      }
    });

    this.observer.observe(this.searchWrapper, { attributes: true, attributeFilter: ['class'] });
  }
}

/* --------------------------------------------------------------------------
   HeaderSafetyNet — Fallback handlers if theme.js HeaderSection fails to init
   Detects whether SearchBar / MobileNavigation initialized, and re-wires
   the three critical features (predictive search, mobile search toggle,
   mobile hamburger) if they didn't.
   -------------------------------------------------------------------------- */

class HeaderSafetyNet {
  constructor() {
    this.headerSection = document.querySelector('[data-section-type="header"]');
    if (!this.headerSection) return;

    this.searchWrapper = this.headerSection.querySelector('.header__search-bar-wrapper');
    this.searchBar = this.searchWrapper ? this.searchWrapper.querySelector('.search-bar') : null;
    this.searchInput = this.searchWrapper ? this.searchWrapper.querySelector('[name="q"]') : null;
    this.mobileMenu = document.getElementById('mobile-menu');
    this.mobileToggle = this.headerSection.querySelector('.header__mobile-nav .header__mobile-nav-toggle[data-action="toggle-menu"]');
    this.searchToggle = this.headerSection.querySelector('[data-action="toggle-search"]');
    this.header = this.headerSection.querySelector('.header');

    // Always activate — handlers are idempotent (read DOM state, don't track booleans).
    // If theme.js also initialized, both handlers read the same DOM state and produce
    // the same outcome, so there's no double-toggle.
    // We delay slightly to let theme.js finish first, so we can detect if it worked
    // and skip the search handler (only search is non-idempotent due to fetch calls).
    setTimeout(() => this._init(), 300);
  }

  _init() {
    this._initPredictiveSearch();
    this._initMobileSearchToggle();
    this._initMobileHamburger();
  }

  _supportsPredictiveApi() {
    try {
      const el = document.getElementById('shopify-features');
      if (!el) return true;
      return JSON.parse(el.innerHTML)['predictiveSearch'];
    } catch (e) {
      return true;
    }
  }

  /* --- Predictive Search (desktop + mobile) --- */

  _initPredictiveSearch() {
    if (!this.searchInput || !this.searchBar) return;

    // Check if theme.js SearchBar already works by dispatching a test input.
    // If theme responds (adds is-loading within 400ms), skip our handler.
    const alreadyWorking = this._probeThemeSearch();
    if (alreadyWorking) return;

    let debounceTimer = null;
    const resultsEl = this.searchWrapper.querySelector('.search-bar__results');
    const resultsInner = this.searchWrapper.querySelector('.search-bar__results-inner');
    const menuWrapper = this.searchWrapper.querySelector('.search-bar__menu-wrapper');

    // Input focus handler — on mobile, skip is-fixed and is-locked entirely
    // to prevent the layout shift (is-fixed changes position: absolute).
    this.searchInput.addEventListener('focusin', () => {
      const isMobile = window.innerWidth < 1000;
      if (!isMobile) {
        this.searchWrapper.classList.add('is-fixed');
        document.documentElement.classList.add('is-locked');
      }

      if (this.searchInput.value === '') {
        if (menuWrapper) menuWrapper.setAttribute('aria-hidden', 'false');
        if (resultsEl) resultsEl.setAttribute('aria-hidden', 'true');
      } else {
        if (menuWrapper) menuWrapper.setAttribute('aria-hidden', 'true');
        if (resultsEl) resultsEl.setAttribute('aria-hidden', 'false');
        this.searchInput.classList.add('is-filled');
      }
      this.searchBar.classList.add('is-expanded');
    });

    // Click outside to close
    document.addEventListener('click', (e) => {
      if (this.searchInput.contains(e.target) || (this.searchBar.querySelector('.search-bar__inner') && this.searchBar.querySelector('.search-bar__inner').contains(e.target))) return;
      if (window.innerWidth < 641) return;

      setTimeout(() => {
        this.searchWrapper.classList.remove('is-fixed');
        document.documentElement.classList.remove('is-locked');
        if (menuWrapper) menuWrapper.setAttribute('aria-hidden', 'true');
        if (resultsEl) resultsEl.setAttribute('aria-hidden', 'true');
        this.searchBar.classList.remove('is-expanded');
      }, 150);
    });

    // Clear button
    const clearBtn = this.searchWrapper.querySelector('[data-action="clear-input"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        this.searchInput.value = '';
        this.searchInput.classList.remove('is-filled');
        if (resultsEl) resultsEl.setAttribute('aria-hidden', 'true');
        if (menuWrapper) menuWrapper.setAttribute('aria-hidden', 'false');
      });
    }

    // Unfix (mobile close) button
    const unfixBtn = this.searchWrapper.querySelector('[data-action="unfix-search"]');
    if (unfixBtn) {
      unfixBtn.addEventListener('click', () => {
        this.searchWrapper.classList.remove('is-fixed');
        this.searchBar.classList.remove('is-expanded');
        if (resultsEl) resultsEl.setAttribute('aria-hidden', 'true');
        this.searchInput.classList.remove('is-filled');
        document.documentElement.classList.remove('is-locked');
        if (menuWrapper) menuWrapper.setAttribute('aria-hidden', 'true');
      });
    }

    // Debounced search on input
    this.searchInput.addEventListener('input', () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        this._doSearch(resultsEl, resultsInner, menuWrapper);
      }, 250);
    });

    // Form submit — add wildcard for better search results
    this.searchBar.addEventListener('submit', () => {
      const clone = this.searchInput.cloneNode();
      clone.setAttribute('type', 'hidden');
      clone.value = this.searchInput.value + '*';
      this.searchInput.removeAttribute('name');
      this.searchInput.insertAdjacentElement('afterend', clone);
    });
  }

  _probeThemeSearch() {
    // Quick non-destructive check: if theme.js SearchBar initialized,
    // it attached a debounced input listener. We can't detect delegated listeners
    // directly, so check if typing triggers is-loading within a tick.
    // We set a test value, dispatch input, wait a microtask, then check.
    const original = this.searchInput.value;
    this.searchInput.value = 'test_probe';
    this.searchInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.searchInput.value = original;
    // Theme's debounce is 250ms, so we can't wait for is-loading here.
    // Instead, check if the theme's delegate has any registered input handlers.
    // The simplest reliable check: look for the ftLabsDelegateIgnore property
    // that the delegate library uses, or check if the searchBar's parentNode
    // has __handler markers. Unfortunately there's no reliable way without
    // inspecting internals.
    //
    // Fallback: always return false (assume theme didn't init) and rely on
    // the fact that duplicate fetches are harmless — the second response just
    // overwrites the first with the same content.
    return false;
  }

  _doSearch(resultsEl, resultsInner, menuWrapper) {
    const currentInput = this.searchInput.value;
    if (currentInput === '') {
      if (menuWrapper) {
        menuWrapper.setAttribute('aria-hidden', 'false');
      } else {
        this.searchBar.classList.remove('is-expanded');
      }
      if (resultsEl) resultsEl.setAttribute('aria-hidden', 'true');
      return;
    }

    if (menuWrapper) menuWrapper.setAttribute('aria-hidden', 'true');
    if (resultsEl) resultsEl.setAttribute('aria-hidden', 'false');
    this.searchBar.classList.add('is-expanded', 'is-loading');

    const typeSelect = this.searchWrapper.querySelector('#search-product-type');
    const productTypeFilter = typeSelect ? typeSelect.value : '';

    const useSuggest = this._supportsPredictiveApi();
    const root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    const url = `${root}search${useSuggest ? '/suggest' : ''}`;
    const productQuery = `${productTypeFilter ? 'product_type:' + productTypeFilter + ' AND ' : ''}${encodeURIComponent(currentInput)}`;

    fetch(`${url}?section_id=predictive-search&q=${productQuery}&resources[limit]=3&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`, {
      method: 'GET',
      credentials: 'same-origin'
    })
    .then(response => response.text())
    .then(text => {
      if (this.searchInput.value !== currentInput) return;

      this.searchBar.classList.remove('is-loading');
      const div = document.createElement('div');
      div.innerHTML = text;
      const ajax = div.querySelector('.search-ajax');
      if (ajax && resultsInner) {
        const content = document.createElement('div');
        content.innerHTML = ajax.innerHTML.trim();
        const viewAll = content.querySelector('.search-bar__view-all');
        if (viewAll) content.insertAdjacentElement('beforeend', viewAll);
        resultsInner.innerHTML = content.innerHTML;
      }
    })
    .catch(() => {
      this.searchBar.classList.remove('is-loading');
    });
  }

  /* --- Mobile Search Toggle --- */
  /* Uses rAF to check if theme.js already handled the click. If theme.js
     changed the state synchronously during bubbling, we skip. Otherwise we act.
     This prevents double-toggle when both theme.js and safety net are active. */

  _initMobileSearchToggle() {
    if (!this.searchToggle || !this.searchWrapper || !this.header) return;

    this.searchToggle.addEventListener('click', (e) => {
      e.preventDefault();
      const stateBefore = this.searchWrapper.classList.contains('is-visible');
      requestAnimationFrame(() => {
        // If theme.js handled it, is-visible would have changed already
        if (this.searchWrapper.classList.contains('is-visible') === stateBefore) {
          if (stateBefore) {
            this.header.classList.remove('header--search-expanded');
            this.searchWrapper.classList.remove('is-visible');
            this.searchToggle.setAttribute('aria-expanded', 'false');
          } else {
            this.header.classList.add('header--search-expanded');
            this.searchWrapper.classList.add('is-visible');
            this.searchToggle.setAttribute('aria-expanded', 'true');
          }
        }
        // Sync aria-expanded with current state (covers theme.js toggle too)
        const isOpen = this.searchWrapper.classList.contains('is-visible');
        this.searchToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }

  /* --- Mobile Hamburger --- */
  /* Same rAF pattern: only act if theme.js didn't already handle the toggle. */

  _initMobileHamburger() {
    if (!this.mobileToggle || !this.mobileMenu) return;

    const toggleMenu = () => {
      const isCurrentlyOpen = this.mobileMenu.getAttribute('aria-hidden') === 'false';
      const newState = !isCurrentlyOpen;

      this.mobileToggle.setAttribute('aria-expanded', newState ? 'true' : 'false');
      this.mobileMenu.setAttribute('aria-hidden', newState ? 'false' : 'true');

      if (newState) {
        document.documentElement.classList.add('is-locked');
      } else {
        document.documentElement.classList.remove('is-locked');
        this.mobileMenu.querySelectorAll('.mobile-menu__panel.is-open').forEach(p => p.classList.remove('is-open'));
      }
    };

    // Hamburger button — deferred toggle to avoid fighting with theme.js
    this.mobileToggle.addEventListener('click', () => {
      const stateBefore = this.mobileMenu.getAttribute('aria-hidden');
      requestAnimationFrame(() => {
        if (this.mobileMenu.getAttribute('aria-hidden') === stateBefore) {
          toggleMenu();
        }
      });
    });

    // Close button inside the mobile menu — also deferred
    this.mobileMenu.querySelectorAll('[data-action="toggle-menu"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const stateBefore = this.mobileMenu.getAttribute('aria-hidden');
        requestAnimationFrame(() => {
          if (this.mobileMenu.getAttribute('aria-hidden') === stateBefore) {
            toggleMenu();
          }
        });
      });
    });

    // Open/close sub-panels
    this.mobileMenu.querySelectorAll('[data-action="open-panel"]').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.setAttribute('aria-expanded', 'true');
        const panel = this.mobileMenu.querySelector('#' + btn.getAttribute('aria-controls'));
        if (panel) panel.classList.add('is-open');
      });
    });

    this.mobileMenu.querySelectorAll('[data-action="close-panel"]').forEach(btn => {
      btn.addEventListener('click', () => {
        const panel = btn.closest('.mobile-menu__panel.is-open');
        if (panel) {
          panel.classList.remove('is-open');
          const opener = this.mobileMenu.querySelector('[aria-controls="' + panel.id + '"]');
          if (opener) opener.setAttribute('aria-expanded', 'false');
        }
      });
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      const isOpen = this.mobileMenu.getAttribute('aria-hidden') === 'false';
      if (isOpen && !this.mobileMenu.contains(e.target) && !this.mobileToggle.contains(e.target)) {
        toggleMenu();
      }
    });
  }
}

function initCustomNavigation() {
  NavBackdrop.init();

  const allCategories = new AllCategoriesDropdown();
  new InlineNavInterceptor(allCategories);
  new CustomMobileMenu();
  new TransparentHeader();
  new CartPriceUpdater();
  new WishlistBadgeUpdater();
  new MobileSearchFix();
  new HeaderSafetyNet();
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCustomNavigation);
} else {
  initCustomNavigation();
}

// Re-initialize on Shopify theme editor section reload
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.querySelector('[data-section-type="header"]')) {
    // Small delay to let theme's own JS initialize first
    setTimeout(initCustomNavigation, 100);
  }
});
